import {
  CreateItemPurchaseOrderRequest,
  CreateIngredientPurchaseOrderRequest,
  DefaultApiFactory,
  Configuration,
  ShadowProxyCancelItemRequest,
  ShadowProxyCancelIngredientRequest,
  FulfillIngredientPurchaseOrderRequest,
  FulfillItemPurchaseOrderRequest,
} from '@ce-openapi/shadow-proxy-purchase-order-ts-axios-client';
import { AxiosError } from 'axios';
import { MOKA_SERVICE_HOST } from 'mf-shared/api/constants';
import {
  CancelIngredientPurchaseOrderProps,
  CancelItemPurchaseOrderProps,
  FulfillIngredientPurchaseOrderProps,
  FulfillItemPurchaseOrderProps,
  PutIngredientPurchaseOrderProps,
  PutItemPurchaseOrderProps,
} from './purchaseOrder.types';

const apiFactory = DefaultApiFactory(new Configuration({}), MOKA_SERVICE_HOST);

const generateError = (error: AxiosError) => {
  if (!error.response) {
    return new Error(error.message);
  }
  const { data } = error.response;
  if (data?.message) {
    return new Error(data.message);
  }
  if (data?.errors) {
    return new Error(data.errors);
  }
  if (data?.error) {
    return new Error(data.error[0]);
  }
  return new Error(error.message);
};

export const postItemPurchaseOrder = async (
  outletId: number,
  requestUuid: string,
  payload: CreateItemPurchaseOrderRequest
) => {
  try {
    const result = await apiFactory.shadowTrafficProxyV1ItemPurchaseOrderPost(
      outletId,
      requestUuid,
      outletId,
      payload
    );
    return result;
  } catch (e) {
    const errorObject = generateError(e);
    throw errorObject;
  }
};

export const postIngredientPurchaseOrder = async (
  outletId: number,
  requestUuid: string,
  payload: CreateIngredientPurchaseOrderRequest
) => {
  try {
    const result =
      await apiFactory.shadowTrafficProxyV1IngredientPurchaseOrderPost(
        requestUuid,
        outletId,
        outletId,
        payload
      );
    return result;
  } catch (e) {
    const errorObject = generateError(e);
    throw errorObject;
  }
};

export const postFulfilledItemPurchaseOrder = async (
  outletId: number,
  requestUuid: string,
  payload: CreateItemPurchaseOrderRequest
) => {
  try {
    const result =
      await apiFactory.shadowTrafficProxyV1ItemPurchaseOrderCreateAndFulfillPost(
        outletId,
        requestUuid,
        outletId,
        payload
      );
    return result;
  } catch (e) {
    const errorObject = generateError(e);
    throw errorObject;
  }
};

export const postFulfilledIngredientPurchaseOrder = async (
  outletId: number,
  requestUuid: string,
  payload: CreateIngredientPurchaseOrderRequest
) => {
  try {
    const result =
      await apiFactory.shadowTrafficProxyV1IngredientPurchaseOrderCreateAndFulfillPost(
        requestUuid,
        outletId,
        outletId,
        payload
      );
    return result;
  } catch (e) {
    const errorObject = generateError(e);
    throw errorObject;
  }
};

export const putItemPurchaseOrder = async (
  props: PutItemPurchaseOrderProps
) => {
  try {
    const { outletId, requestUuid, purchaseOrderId, payload } = props;
    const result = await apiFactory.shadowTrafficProxyV1ItemPurchaseOrderIdPut(
      outletId,
      requestUuid,
      purchaseOrderId,
      outletId,
      payload
    );
    return result;
  } catch (e) {
    const errorObject = generateError(e);
    throw errorObject;
  }
};

export const putIngredientPurchaseOrder = async (
  props: PutIngredientPurchaseOrderProps
) => {
  try {
    const { outletId, requestUuid, purchaseOrderId, payload } = props;
    const result =
      await apiFactory.shadowTrafficProxyV1IngredientPurchaseOrderIdPut(
        requestUuid,
        outletId,
        purchaseOrderId,
        outletId,
        payload
      );
    return result;
  } catch (e) {
    const errorObject = generateError(e);
    throw errorObject;
  }
};

export const fulfillItemPurchaseOrder = async (
  props: FulfillItemPurchaseOrderProps
) => {
  try {
    const { outletId, requestUuid, purchaseOrderId, payload } = props;

    const purchaseOrderPayload: FulfillItemPurchaseOrderRequest = {
      item_purchase_order_legacy: {
        ...payload.item_purchase_order_legacy,
        order_no: null,
      },
      item_purchase_order: {
        ...payload.item_purchase_order,
        order_no: null,
      },
    };

    const result =
      await apiFactory.shadowTrafficProxyV1ItemPurchaseOrderIdFulfillPut(
        outletId,
        requestUuid,
        purchaseOrderId,
        outletId,
        purchaseOrderPayload
      );
    return result;
  } catch (e) {
    const errorObject = generateError(e);
    throw errorObject;
  }
};

export const fulfillIngredientPurchaseOrder = async (
  props: FulfillIngredientPurchaseOrderProps
) => {
  try {
    const { outletId, requestUuid, purchaseOrderId, payload } = props;

    const purchaseOrderPayload: FulfillIngredientPurchaseOrderRequest = {
      ingredient_purchase_order_legacy: {
        ...payload.ingredient_purchase_order_legacy,
        order_no: null,
      },
      ingredient_purchase_order: {
        ...payload.ingredient_purchase_order,
        order_no: null,
      },
    };

    const result =
      await apiFactory.shadowTrafficProxyV1IngredientPurchaseOrderIdFulfillPut(
        requestUuid,
        outletId,
        purchaseOrderId,
        outletId,
        purchaseOrderPayload
      );
    return result;
  } catch (e) {
    const errorObject = generateError(e);
    throw errorObject;
  }
};

export const cancelItemPurchaseOrder = async (
  props: CancelItemPurchaseOrderProps
) => {
  try {
    const { outletId, requestUuid, purchaseOrderId, payload } = props;

    const purchaseOrderPayload: ShadowProxyCancelItemRequest = {
      item_purchase_order_legacy: {
        ...payload.item_purchase_order_legacy,
        order_no: null,
      },
    };

    const result =
      await apiFactory.shadowTrafficProxyV1ItemPurchaseOrderIdCancelPut(
        outletId,
        requestUuid,
        purchaseOrderId,
        outletId,
        purchaseOrderPayload
      );
    return result;
  } catch (e) {
    const errorObject = generateError(e);
    throw errorObject;
  }
};

export const cancelIngredientPurchaseOrder = async (
  props: CancelIngredientPurchaseOrderProps
) => {
  try {
    const { outletId, requestUuid, purchaseOrderId, userId, payload } = props;

    const purchaseOrderPayload: ShadowProxyCancelIngredientRequest = {
      ingredient_purchase_order_legacy: {
        ...payload.ingredient_purchase_order_legacy,
        order_no: null,
      },
    };

    const result =
      await apiFactory.shadowTrafficProxyV1IngredientPurchaseOrderIdCancelPut(
        outletId,
        purchaseOrderId,
        requestUuid,
        userId,
        outletId,
        purchaseOrderPayload
      );
    return result;
  } catch (e) {
    const errorObject = generateError(e);
    throw errorObject;
  }
};
